import { memo } from "react";
import { styled } from "@mui/material/styles";
import type { AdmissionCode } from "@shared/types/rejections.ts";
import { explainAdmissionFinding } from "../../../../lib/admissionFindingExplanation";
import { Caption } from "../../../primitives/data-display/Caption";
import { AdmissionFindingBadge } from "../AdmissionFindingBadge";

export interface RejectionFindingSummaryProps {
  code: AdmissionCode;
  /** The gate's explanation; omitted or null when none was recorded. */
  message?: string | null;
}

const FindingLines = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
  textAlign: "left",
});

/** Gate messages often restate their code as a prefix ("evidence.not_primary: …"); the code is
 *  already shown by the badge, so it is dropped from the message. */
function messageWithoutCode(code: string, message: string): string {
  const prefix = `${code}:`;
  return message.startsWith(prefix) ? message.slice(prefix.length).trim() : message;
}

/** A rejection finding at a glance: its admission code badge, with the gate's explanation beneath,
 *  put in plain words when it is a coverage finding. */
export const RejectionFindingSummary = memo(function RejectionFindingSummary({
  code,
  message,
}: RejectionFindingSummaryProps) {
  const explanation = message
    ? (explainAdmissionFinding(code, message) ?? messageWithoutCode(code, message))
    : "";
  return (
    <FindingLines>
      <AdmissionFindingBadge code={code} />
      {explanation && <Caption>{explanation}</Caption>}
    </FindingLines>
  );
});
